import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import { useSettings } from '../../contexts/SettingsContext';
import api from '../../services/api';
import { showToast } from '../../components/ui/Toast';
import { uploadToImgBB } from '../../utils/imgbb';

const Checkout = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { settings } = useSettings();
  const { items, subtotalUSD, subtotalBS, deliveryFeeUSD, deliveryFeeBS, totalUSD, totalBS, clearCart } = useCart();

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [step, setStep] = useState(1);
  const [orderCreated, setOrderCreated] = useState(null);
  const [comprobante, setComprobante] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [location, setLocation] = useState({ lat: null, lng: null });
  const [gettingLocation, setGettingLocation] = useState(false);

  const [formData, setFormData] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    email: user?.email || '',
    address: '',
    notes: '',
    paymentMethod: 'pago_movil',
    paymentPhone: '',
    paymentBank: '',
    paymentReference: '',
    paymentAmount: ''
  });

  useEffect(() => {
    if (items.length === 0 && !orderCreated) {
      navigate('/carrito');
    }
  }, [items, orderCreated, navigate]);

  const paymentMethods = [
    { id: 'pago_movil', name: 'Pago Móvil', icon: '📱', description: 'Transferencia inmediata' },
    { id: 'transferencia', name: 'Transferencia', icon: '🏦', description: 'Transferencia bancaria' },
    { id: 'zelle', name: 'Zelle', icon: '💵', description: 'Pago en dólares' },
    { id: 'efectivo', name: 'Efectivo', icon: '💰', description: 'Pago contra entrega' }
  ];

  const paymentInfo = {
    pago_movil: {
      bank: settings?.payment_bank || 'Banesco',
      phone: settings?.payment_phone || '04141234567',
      account: settings?.payment_account || '123456789',
      holder: settings?.payment_account_holder || 'Dulce 3D C.A.'
    },
    transferencia: {
      bank: settings?.payment_bank || 'Banesco',
      account: settings?.payment_account || '123456789',
      holder: settings?.payment_account_holder || 'Dulce 3D C.A.'
    },
    zelle: {
      email: settings?.payment_email || 'pagos@tupastel.com',
      holder: settings?.payment_account_holder || 'Dulce 3D'
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        showToast('El archivo no debe superar 5MB', 'error');
        return;
      }
      setComprobante(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  // ===== OBTENER UBICACIÓN GPS =====
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      showToast('Tu navegador no soporta GPS', 'error');
      return;
    }

    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
        showToast('📍 Ubicación capturada', 'success');
        setGettingLocation(false);
      },
      (error) => {
        console.error('Error GPS:', error);
        showToast('No se pudo obtener la ubicación. Verifica los permisos.', 'error');
        setGettingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleCreateOrder = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.address) {
      showToast('Completa los campos obligatorios', 'error');
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.post('/orders', {
        items: items.map(item => ({ productId: item.id, quantity: item.quantity })),
        delivery: {
          address: formData.address,
          lat: location.lat,
          lng: location.lng
        },
        customerInfo: {
          name: formData.name,
          phone: formData.phone,
          email: formData.email || user?.email || '',
          notes: formData.notes
        },
        paymentMethod: formData.paymentMethod,
        comments: formData.notes
      });

      setOrderCreated(data.data);
      setStep(2);
      showToast('✅ Pedido creado', 'success');
    } catch (error) {
      showToast(error.response?.data?.message || 'Error al crear pedido', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();

    if (formData.paymentMethod === 'efectivo') {
      await finishOrder();
      return;
    }

    if (formData.paymentMethod === 'pago_movil' && !formData.paymentPhone) {
      showToast('Ingresa el teléfono desde el que pagaste', 'error');
      return;
    }
    if (!formData.paymentReference) {
      showToast('Ingresa el número de referencia', 'error');
      return;
    }

    setLoading(true);
    setUploading(true);
    
    try {
      let comprobanteUrl = null;
      
      // Subir a imgBB si hay comprobante
      if (comprobante) {
        showToast('📤 Subiendo comprobante...', 'info');
        const upload = await uploadToImgBB(comprobante);
        
        if (upload.success) {
          comprobanteUrl = upload.url;
          showToast('✅ Comprobante subido', 'success');
        } else {
          showToast('Error al subir comprobante: ' + upload.error, 'error');
          setLoading(false);
          setUploading(false);
          return;
        }
      }
      
      setUploading(false);

      // Enviar al backend
      await api.post(`/orders/${orderCreated.id}/upload-comprobante`, {
        paymentPhone: formData.paymentPhone || '',
        paymentBank: formData.paymentBank || '',
        paymentReference: formData.paymentReference || '',
        paymentAmount: formData.paymentAmount || totalBS,
        comprobanteUrl: comprobanteUrl
      });

      await finishOrder();
    } catch (error) {
      showToast(error.response?.data?.message || 'Error al enviar comprobante', 'error');
    } finally {
      setLoading(false);
      setUploading(false);
    }
  };

  const finishOrder = async () => {
    clearCart();
    setStep(3);
    showToast('🎉 Pedido enviado', 'success');
    setTimeout(() => {
      navigate(`/pedido/${orderCreated.id}`);
    }, 3000);
  };

  if (items.length === 0 && step !== 3) {
    return null;
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Indicador de pasos */}
      <div className="flex items-center justify-center mb-8">
        {[1, 2, 3].map((s, i) => (
          <React.Fragment key={s}>
            <div className={`flex items-center ${step >= s ? 'text-gold' : 'text-gray-300'}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${step >= s ? 'bg-gold text-white' : 'bg-gray-200'}`}>
                {step > s ? '✓' : s}
              </div>
              <span className="ml-2 font-medium hidden sm:inline">
                {s === 1 ? 'Datos' : s === 2 ? 'Pago' : 'Confirmación'}
              </span>
            </div>
            {i < 2 && <div className={`w-12 sm:w-16 h-0.5 mx-2 ${step > s ? 'bg-gold' : 'bg-gray-200'}`} />}
          </React.Fragment>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          {/* PASO 1 */}
          {step === 1 && (
            <form onSubmit={handleCreateOrder} className="space-y-4">
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <h2 className="text-xl font-semibold mb-4">📋 Información de contacto</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Nombre *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-gold"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Teléfono *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-gold"
                      required
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium mb-1">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-gold"
                  />
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <h2 className="text-xl font-semibold mb-4">📍 Dirección de entrega</h2>
                <div>
                  <label className="block text-sm font-medium mb-1">Dirección completa *</label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    rows="3"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-gold"
                    placeholder="Calle, número, referencia, ciudad"
                    required
                  />
                </div>
                
                {/* GPS */}
                <button
                  type="button"
                  onClick={getCurrentLocation}
                  disabled={gettingLocation}
                  className={`mt-3 w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 transition ${
                    location.lat 
                      ? 'bg-green-50 border-green-500 text-green-700' 
                      : 'border-gray-200 hover:border-gold text-gray-700'
                  }`}
                >
                  {gettingLocation ? (
                    <>
                      <div className="w-4 h-4 border-2 border-gold border-t-transparent rounded-full animate-spin" />
                      Obteniendo ubicación...
                    </>
                  ) : location.lat ? (
                    <>✅ Ubicación capturada ({location.lat.toFixed(4)}, {location.lng.toFixed(4)})</>
                  ) : (
                    <>📍 Usar mi ubicación actual (recomendado)</>
                  )}
                </button>

                <div className="mt-4">
                  <label className="block text-sm font-medium mb-1">Notas adicionales</label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows="2"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-gold"
                    placeholder="Instrucciones especiales"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gold text-white py-4 rounded-full font-semibold text-lg hover:bg-gold-dark transition shadow-md disabled:opacity-50"
              >
                {loading ? 'Creando pedido...' : 'Continuar al pago →'}
              </button>
            </form>
          )}

          {/* PASO 2 */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <h2 className="text-xl font-semibold mb-4">💳 Método de pago</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {paymentMethods.map(method => (
                    <button
                      key={method.id}
                      type="button"
                      onClick={() => setFormData({ ...formData, paymentMethod: method.id })}
                      className={`p-4 rounded-xl border-2 transition-all text-center ${
                        formData.paymentMethod === method.id
                          ? 'border-gold bg-gold/5 shadow-md scale-105'
                          : 'border-gray-200 hover:border-gold/50'
                      }`}
                    >
                      <div className="text-3xl mb-1">{method.icon}</div>
                      <div className="font-semibold text-sm">{method.name}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-200 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-blue-900 mb-4">
                  {paymentMethods.find(m => m.id === formData.paymentMethod)?.icon}{' '}
                  Datos para {paymentMethods.find(m => m.id === formData.paymentMethod)?.name}
                </h3>

                <div className="bg-white rounded-xl p-4 space-y-2">
                  {formData.paymentMethod === 'pago_movil' && (
                    <>
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-gray-600 text-sm">Banco</span>
                        <span className="font-semibold">{paymentInfo.pago_movil.bank}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-gray-600 text-sm">Teléfono</span>
                        <span className="font-semibold">{paymentInfo.pago_movil.phone}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-gray-600 text-sm">Cédula/RIF</span>
                        <span className="font-semibold">{paymentInfo.pago_movil.account}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-gray-600 text-sm">Titular</span>
                        <span className="font-semibold">{paymentInfo.pago_movil.holder}</span>
                      </div>
                    </>
                  )}

                  {formData.paymentMethod === 'transferencia' && (
                    <>
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-gray-600 text-sm">Banco</span>
                        <span className="font-semibold">{paymentInfo.transferencia.bank}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-gray-600 text-sm">Cuenta</span>
                        <span className="font-semibold">{paymentInfo.transferencia.account}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-gray-600 text-sm">Titular</span>
                        <span className="font-semibold">{paymentInfo.transferencia.holder}</span>
                      </div>
                    </>
                  )}

                  {formData.paymentMethod === 'zelle' && (
                    <>
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-gray-600 text-sm">Email</span>
                        <span className="font-semibold">{paymentInfo.zelle.email}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-gray-600 text-sm">Titular</span>
                        <span className="font-semibold">{paymentInfo.zelle.holder}</span>
                      </div>
                    </>
                  )}

                  <div className="flex justify-between items-center py-2 bg-gold/10 rounded-lg px-3 -mx-1 mt-2">
                    <span className="font-medium">Total a pagar</span>
                    <div className="text-right">
                      <div className="font-bold text-gold text-xl">
                        {formData.paymentMethod === 'zelle' || formData.paymentMethod === 'efectivo'
                          ? `$${totalUSD.toFixed(2)}`
                          : `Bs ${totalBS.toFixed(2)}`}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <form onSubmit={handlePaymentSubmit} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <h2 className="text-xl font-semibold mb-4">📸 Confirma tu pago</h2>

                {formData.paymentMethod !== 'efectivo' && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      {formData.paymentMethod === 'pago_movil' && (
                        <div>
                          <label className="block text-sm font-medium mb-1">Teléfono emisor *</label>
                          <input
                            type="tel"
                            name="paymentPhone"
                            value={formData.paymentPhone}
                            onChange={handleChange}
                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-gold"
                            placeholder="04141234567"
                            required
                          />
                        </div>
                      )}
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          {formData.paymentMethod === 'zelle' ? 'Email emisor *' : 'Banco emisor *'}
                        </label>
                        <input
                          type="text"
                          name="paymentBank"
                          value={formData.paymentBank}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-gold"
                          placeholder={formData.paymentMethod === 'zelle' ? 'tu@email.com' : 'Banesco'}
                          required
                        />
                      </div>
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium mb-1">Número de referencia *</label>
                      <input
                        type="text"
                        name="paymentReference"
                        value={formData.paymentReference}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-gold"
                        placeholder="Ej: 123456789"
                        required
                      />
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium mb-2">Comprobante *</label>
                      <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-gold transition cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleFileChange}
                          className="hidden"
                          id="comprobante-input"
                        />
                        <label htmlFor="comprobante-input" className="cursor-pointer block">
                          {previewUrl ? (
                            <div>
                              <img src={previewUrl} alt="Comprobante" className="max-h-40 mx-auto rounded-lg mb-2" />
                              <p className="text-sm text-green-600 font-medium">✅ Archivo listo para subir</p>
                              <p className="text-xs text-gray-500 mt-1">Clic para cambiar</p>
                            </div>
                          ) : (
                            <div>
                              <div className="text-5xl mb-2">📷</div>
                              <p className="text-sm font-medium text-gray-700">Toca para subir tu comprobante</p>
                              <p className="text-xs text-gray-500 mt-1">JPG, PNG (máx. 5MB)</p>
                            </div>
                          )}
                        </label>
                      </div>
                    </div>
                  </>
                )}

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="py-4 px-6 border-2 border-gray-200 text-gray-600 rounded-full font-semibold hover:bg-gray-50 transition"
                  >
                    ← Volver
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 bg-gold text-white py-4 rounded-full font-semibold hover:bg-gold-dark transition shadow-md disabled:opacity-50"
                  >
                    {uploading ? '📤 Subiendo...' : loading ? 'Enviando...' : '✓ Confirmar pago'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* PASO 3 */}
          {step === 3 && (
            <div className="bg-white rounded-2xl p-12 shadow-sm border border-gray-100 text-center">
              <div className="inline-block w-20 h-20 border-4 border-gold border-t-transparent rounded-full animate-spin mb-6" />
              <h2 className="text-2xl font-serif font-bold mb-3">⏳ Estamos procesando tu pago</h2>
              <p className="text-gray-500 mb-6">Verificaremos tu comprobante y confirmaremos tu pedido.</p>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-left">
                <p className="text-sm text-blue-800 mb-2"><strong>📋 ¿Qué sigue?</strong></p>
                <ol className="text-sm text-blue-700 space-y-1 ml-4 list-decimal">
                  <li>Verificamos tu comprobante (5-15 min)</li>
                  <li>Confirmamos tu pedido</li>
                  <li>Comenzamos a preparar tu pedido</li>
                  <li>Un motorizado lo llevará a tu dirección</li>
                </ol>
              </div>
            </div>
          )}
        </div>

        {/* Resumen */}
        {step !== 3 && (
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 sticky top-24">
              <h2 className="text-lg font-serif font-bold mb-4">🛒 Tu pedido</h2>
              <div className="space-y-2 max-h-60 overflow-y-auto mb-4">
                {items.map(item => (
                  <div key={item.id} className="flex justify-between text-sm">
                    <span>{item.quantity}x {item.name}</span>
                    <span className="font-medium">${(Number(item.price) * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>

              <div className="border-t pt-3 space-y-2 text-sm">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span>
                  <div className="text-right">
                    <div>${subtotalUSD.toFixed(2)}</div>
                    <div className="text-xs">Bs {subtotalBS.toFixed(2)}</div>
                  </div>
                </div>
                {deliveryFeeUSD > 0 && (
                  <div className="flex justify-between text-gray-600">
                    <span>Envío</span>
                    <span>${deliveryFeeUSD.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center pt-3 border-t">
                  <span className="font-bold">Total</span>
                  <div className="text-right">
                    <div className="text-xl font-bold text-gold">${totalUSD.toFixed(2)}</div>
                    <div className="text-xs text-gray-600">Bs {totalBS.toFixed(2)}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Checkout;
